/**
 * Relative centrifugal force (RCF, "× g") and rotor speed.
 *
 * RCF = ω²r ÷ g with ω = 2π × rpm ÷ 60. With r in centimetres and standard gravity
 * 980.665 cm/s² that is  RCF = 1.118 × 10⁻⁵ × r × rpm²  (the constant is
 * (2π ÷ 60)² ÷ 980.665 = 1.1183 × 10⁻⁵, rounded the way rotor manuals round it). With r in
 * millimetres the constant is 1.118 × 10⁻⁶ — the form Beckman Coulter prints.
 */

export type RadiusUnit = "cm" | "mm" | "in";

const CONSTANT_PER_CM = 1.118e-5;
const CM_PER_UNIT: Record<RadiusUnit, number> = {
  cm: 1,
  mm: 0.1,
  in: 2.54,
};

/** The multiplier in front of r × rpm² for a radius written in `unit`. */
export function rcfConstant(unit: RadiusUnit): number {
  return CONSTANT_PER_CM * CM_PER_UNIT[unit];
}

export function rcfFromRpm(
  rpm: number,
  radius: number,
  unit: RadiusUnit
): number {
  return rcfConstant(unit) * radius * rpm * rpm;
}

export function rpmFromRcf(
  rcf: number,
  radius: number,
  unit: RadiusUnit
): number {
  return Math.sqrt(rcf / (rcfConstant(unit) * radius));
}

export type CentrifugeError = "rpm" | "rcf" | "radius" | "surface";

/** Speeds in common protocols, in × g. */
export const COMMON_RCF: readonly number[] = [
  300, 500, 1000, 3000, 5000, 10_000, 12_000, 16_000,
];

export function roundRpm(rpm: number, step: number): number {
  return Math.round(rpm / step) * step;
}

export interface SpeedRow {
  rcf: number;
  rpm: number;
}

export function commonSpeeds(radius: number, unit: RadiusUnit): SpeedRow[] {
  return COMMON_RCF.map((rcf) => ({ rcf, rpm: rpmFromRcf(rcf, radius, unit) }));
}
